import type { AgentEvent, Card, ConversationDetail } from '@ctd/agent-contract';

export type AgentPhase = 'working' | 'answering' | 'done' | 'stopped' | 'error';

export type AgentCallStatus = 'running' | 'done' | 'failed';

export interface AgentCall {
    id: string;
    kpi: string;
    summary: string;
    status: AgentCallStatus;
    card: Card | null;
}

/** One question and the agent's whole answer to it. */
export interface AgentTurn {
    question: string;
    phase: AgentPhase;
    calls: AgentCall[];
    answer: string;
}

export interface AgentThread {
    conversationId: string | null;
    turns: AgentTurn[];
    /** Stored turns older than the first of `turns`, which the API left out. */
    earlierTurns: number;
}

export function isTurnActive(phase: AgentPhase): boolean {
    return phase === 'working' || phase === 'answering';
}

export function emptyThread(): AgentThread {
    return { conversationId: null, turns: [], earlierTurns: 0 };
}

function updateLastTurn(
    thread: AgentThread,
    update: (turn: AgentTurn) => AgentTurn
): AgentThread {
    const index = thread.turns.length - 1;
    if (index < 0) {
        return thread;
    }
    return {
        ...thread,
        turns: thread.turns.map((turn, position) =>
            position === index ? update(turn) : turn
        ),
    };
}

function updateCall(
    turn: AgentTurn,
    id: string,
    update: Partial<AgentCall>
): AgentTurn {
    if (!turn.calls.some((call) => call.id === id)) {
        return turn;
    }
    return {
        ...turn,
        calls: turn.calls.map((call) =>
            call.id === id ? { ...call, ...update } : call
        ),
    };
}

function applyToTurn(turn: AgentTurn, event: AgentEvent): AgentTurn {
    switch (event.type) {
        case 'started':
            return turn;
        case 'tool_call':
            return {
                ...turn,
                calls: [
                    ...turn.calls,
                    {
                        id: event.id,
                        kpi: event.kpi,
                        summary: event.summary,
                        status: 'running',
                        card: null,
                    },
                ],
            };
        case 'result':
            return updateCall(turn, event.toolCallId, {
                status: 'done',
                card: event.card,
            });
        case 'tool_error':
            return updateCall(turn, event.toolCallId, { status: 'failed' });
        case 'final_answer_tokens':
            return {
                ...turn,
                phase: 'answering',
                answer: turn.answer + event.text,
            };
        case 'error':
            return { ...turn, phase: 'error' };
        case 'done':
            return {
                ...turn,
                phase: 'done',
                calls: turn.calls.map((call) =>
                    call.status === 'running'
                        ? { ...call, status: 'failed' }
                        : call
                ),
            };
    }
}

/** Pure: the request was cancelled (the overlay closed) before the turn finished. */
export function stopTurn(thread: AgentThread): AgentThread {
    return updateLastTurn(thread, (turn) =>
        isTurnActive(turn.phase) ? { ...turn, phase: 'stopped' } : turn
    );
}

/** Pure: the request failed, or the stream ended before the turn did. */
export function failTurn(thread: AgentThread): AgentThread {
    return updateLastTurn(thread, (turn) =>
        isTurnActive(turn.phase) ? { ...turn, phase: 'error' } : turn
    );
}

/** Pure: a new question joins the thread; a turn still running is left stopped. */
export function startTurn(thread: AgentThread, question: string): AgentThread {
    const settled = stopTurn(thread);
    return {
        ...settled,
        turns: [
            ...settled.turns,
            { question, phase: 'working', calls: [], answer: '' },
        ],
    };
}

/** Pure: the conversation no longer exists on the server; the next question starts a new one. */
export function forgetConversation(thread: AgentThread): AgentThread {
    return { ...thread, conversationId: null };
}

/**
 * Pure: fold one event into the thread. `started` fixes the current conversation; the
 * others go to the last turn, which ignores them once it is finished.
 */
export function applyAgentEvent(
    thread: AgentThread,
    event: AgentEvent
): AgentThread {
    const turn = thread.turns[thread.turns.length - 1];
    if (!turn || !isTurnActive(turn.phase)) {
        return thread;
    }
    const withConversation =
        event.type === 'started'
            ? { ...thread, conversationId: event.conversationId }
            : thread;
    return updateLastTurn(withConversation, (last) => applyToTurn(last, event));
}

const PHASE_OF_STATUS = {
    completed: 'done',
    interrupted: 'stopped',
    error: 'error',
} as const;

/**
 * The thread as the API stored it. A turn with no stored answer reads as stopped. A call with
 * neither card nor error fails in a completed turn, as it does live at `done`; in any other turn
 * it was cut short, so it stays `running` (no restored phase animates it).
 */
export function threadFromConversation(
    detail: ConversationDetail
): AgentThread {
    return {
        conversationId: detail.id,
        earlierTurns: detail.earlierTurns,
        turns: detail.turns.map(({ question, answer }) => ({
            question,
            phase: answer ? PHASE_OF_STATUS[answer.status] : 'stopped',
            answer: answer?.text ?? '',
            calls: (answer?.calls ?? []).map((call) => ({
                id: call.id,
                kpi: call.kpi,
                summary: call.summary,
                status: call.card
                    ? 'done'
                    : call.error !== undefined || answer?.status === 'completed'
                    ? 'failed'
                    : 'running',
                card: call.card ?? null,
            })),
        })),
    };
}
